/**
 * responsiveColumns.ts
 *
 * The column set a width-observing list page should hand to `DataTable`.
 *
 * ── Why this exists (SCRUM-1447) ────────────────────────────────────────────────
 * Two pages watch their table container with a `ResizeObserver` and drop columns from the
 * right as it narrows. That predates card mode and was a reasonable answer for a table.
 *
 * `DataTable` builds a card's labelled fields from the *same* `columns` array it builds the
 * table from. So a page that filters upstream filters the card too — and a card has no
 * header row and no horizontal scroll, so those fields are not merely narrow, they are
 * unreachable. On Leads at 375px that removed Subject, Origin and Facility, which is the
 * report: "the user cannot tell where a lead came from or which facility it belongs to
 * without opening each lead", against rows with unused white space on the right.
 *
 * The fix is one branch, and it lives here rather than inline so the two pages and the test
 * cannot drift: below the card breakpoint the thresholds do not apply at all, because
 * stacked fields do not compete for width.
 */
import type { DataTableColumn } from './DataTable';

/**
 * `[width, key]` — hide `key` while the container is narrower than `width`.
 *
 * Ordered widest-first by convention so the removal order reads off the literal, but the
 * function does not depend on the order.
 */
export type ColumnWidthThresholds = ReadonlyArray<readonly [width: number, key: string]>;

export function columnsForContainerWidth<T>(
  all: DataTableColumn<T>[],
  containerWidth: number,
  hideBelow: ColumnWidthThresholds,
  asCards: boolean,
): DataTableColumn<T>[] {
  // The whole point. Not `hideBelow` with a lower floor, and not a wider threshold set —
  // a card stacks, so there is no width to reclaim and nothing to trade away.
  if (asCards) return all;

  const hidden = new Set(
    hideBelow.filter(([width]) => containerWidth < width).map(([, key]) => key),
  );

  // Same array identity when nothing is hidden, so a memo downstream is not invalidated on
  // every observer tick at a width where no threshold is crossed.
  return hidden.size === 0 ? all : all.filter((col) => !hidden.has(col.key as string));
}
